import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Badge, Spinner } from 'react-bootstrap';

const CategoryPricing = ({ categoryName, categoryId, selectedCountry, onSave, onFeeUpdated }) => {
    const [formData, setFormData] = useState({
        countryCode: '',
        msrpMultiplier: '',
        finalMultiplier: '',
        premiumListing: false,
        premiumMsrp: '',
        premiumMsrpMultiplier: '',
        premiumFinalMultiplier: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [existingFee, setExistingFee] = useState(null);

    // Country configuration
    const countries = [
        { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
        { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
        { code: 'CL', name: 'Chile', flag: '🇨🇱' },
        { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
        { code: 'CO', name: 'Colombia', flag: '🇨🇴' }
    ];

    const API_BASE_URL = 'https://h4gqwxd22m.execute-api.us-east-1.amazonaws.com/beta/integration/meli-fees';

    // Load existing fee when country changes
    const loadExistingFee = async (countryCode) => {
        if (!countryCode || !categoryId) return;

        setIsLoading(true);
        setError(null);
        setExistingFee(null);

        try {
            console.log(`Loading fee for country: ${countryCode}, category: ${categoryId}`);

            const response = await fetch(
                `${API_BASE_URL}?country_code=${countryCode}&meli_category_id=${categoryId}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('API response:', data);

            if (data.success && data.data) {
                // Fee exists, populate the form
                setExistingFee(data.data);
                setFormData(prev => ({
                    ...prev,
                    msrpMultiplier: data.data.msrp_multiplier.toString(),
                    finalMultiplier: data.data.final_multiplier.toString(),
                    premiumListing: data.data.premium_listing === 1,
                    premiumMsrp: data.data.premium_msrp ? data.data.premium_msrp.toString() : '',
                    premiumMsrpMultiplier: data.data.premium_msrp_multiplier ? data.data.premium_msrp_multiplier.toString() : '',
                    premiumFinalMultiplier: data.data.premium_final_multiplier ? data.data.premium_final_multiplier.toString() : ''
                }));
            } else {
                // No fee exists, clear the form
                setExistingFee(null);
                setFormData(prev => ({
                    ...prev,
                    msrpMultiplier: '',
                    finalMultiplier: '',
                    premiumListing: false,
                    premiumMsrp: '',
                    premiumMsrpMultiplier: '',
                    premiumFinalMultiplier: ''
                }));
            }
        } catch (err) {
            console.error('Error loading existing fee:', err);
            setError('Failed to load existing fee data');
        } finally {
            setIsLoading(false);
        }
    };

    // Update country when selectedCountry prop changes
    useEffect(() => {
        if (selectedCountry && selectedCountry !== formData.countryCode) {
            setFormData(prev => ({ ...prev, countryCode: selectedCountry }));
            loadExistingFee(selectedCountry);
        }
    }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize with selectedCountry prop
    useEffect(() => {
        if (selectedCountry && categoryId) {
            setFormData(prev => ({ ...prev, countryCode: selectedCountry }));
            loadExistingFee(selectedCountry);
        }
    }, [selectedCountry, categoryId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Remove the auto-selection of first country since it's now controlled by parent
    // useEffect(() => {
    //     if (countries.length > 0 && categoryId) {
    //         const firstCountry = countries[0].code;
    //         console.log('Setting initial country:', firstCountry);
    //         setFormData(prev => ({ ...prev, countryCode: firstCountry }));
    //         loadExistingFee(firstCountry);
    //     }
    // }, [categoryId]);

    const handleCountryChange = (countryCode) => {
        console.log('Country changed to:', countryCode);
        setFormData(prev => ({ ...prev, countryCode }));
        loadExistingFee(countryCode);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePremiumListingChange = (checked) => {
        if (checked) {
            // Enabling premium listing - restore values from existingFee if available
            setFormData(prev => ({
                ...prev,
                premiumListing: checked,
                // Restore premium fields from existingFee if they exist
                premiumMsrp: existingFee?.premium_msrp ? existingFee.premium_msrp.toString() : prev.premiumMsrp,
                premiumMsrpMultiplier: existingFee?.premium_msrp_multiplier ? existingFee.premium_msrp_multiplier.toString() : prev.premiumMsrpMultiplier,
                premiumFinalMultiplier: existingFee?.premium_final_multiplier ? existingFee.premium_final_multiplier.toString() : prev.premiumFinalMultiplier
            }));
        } else {
            // Disabling premium listing - clear the fields but preserve them in state for potential re-enabling
            setFormData(prev => ({
                ...prev,
                premiumListing: checked
                // Don't clear the fields - they'll be hidden by the UI but preserved
            }));
        }
    };

    const handleSave = async () => {
        if (!formData.countryCode || !formData.msrpMultiplier || !formData.finalMultiplier) {
            setError('Please fill in all required fields');
            return;
        }

        // Validate standard multipliers
        const msrpMultiplier = parseFloat(formData.msrpMultiplier);
        const finalMultiplier = parseFloat(formData.finalMultiplier);

        if (msrpMultiplier <= 0 || finalMultiplier <= 0) {
            setError('Multipliers must be greater than 0');
            return;
        }

        // Validate premium listing parameters if enabled
        if (formData.premiumListing) {
            if (!formData.premiumMsrp || !formData.premiumMsrpMultiplier || !formData.premiumFinalMultiplier) {
                setError('Premium listing requires all premium fields to be filled');
                return;
            }

            const premiumMsrp = parseFloat(formData.premiumMsrp);
            const premiumMsrpMultiplier = parseFloat(formData.premiumMsrpMultiplier);
            const premiumFinalMultiplier = parseFloat(formData.premiumFinalMultiplier);

            if (premiumMsrp <= 0 || premiumMsrpMultiplier <= 0 || premiumFinalMultiplier <= 0) {
                setError('Premium parameters must be greater than 0');
                return;
            }
        }

        setIsSaving(true);
        setError(null);

        try {
            const requestBody = {
                country_code: formData.countryCode,
                meli_category_id: categoryId,
                msrp_multiplier: msrpMultiplier,
                final_multiplier: finalMultiplier,
                premium_listing: formData.premiumListing ? 1 : 0,
                premium_msrp: formData.premiumListing ? parseFloat(formData.premiumMsrp) : null,
                premium_msrp_multiplier: formData.premiumListing ? parseFloat(formData.premiumMsrpMultiplier) : null,
                premium_final_multiplier: formData.premiumListing ? parseFloat(formData.premiumFinalMultiplier) : null
            };

            console.log('Saving fee with data:', requestBody);

            const response = await fetch(API_BASE_URL, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Save response:', data);

            if (data.success) {
                setSuccess(true);
                setIsEditing(false);

                // Update existing fee data with new fields
                setExistingFee({
                    country_code: formData.countryCode,
                    meli_category_id: categoryId,
                    msrp_multiplier: msrpMultiplier,
                    final_multiplier: finalMultiplier,
                    premium_listing: formData.premiumListing ? 1 : 0,
                    premium_msrp: formData.premiumListing ? parseFloat(formData.premiumMsrp) : null,
                    premium_msrp_multiplier: formData.premiumListing ? parseFloat(formData.premiumMsrpMultiplier) : null,
                    premium_final_multiplier: formData.premiumListing ? parseFloat(formData.premiumFinalMultiplier) : null,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    // Preserve shipping fees data
                    shipping_fees: existingFee?.shipping_fees || []
                });

                // Call parent callbacks
                if (onSave) {
                    onSave({
                        ...formData,
                        existingFee: data
                    });
                }

                if (onFeeUpdated) {
                    onFeeUpdated({
                        countryCode: formData.countryCode,
                        categoryId: categoryId,
                        message: data.message,
                        clearPremiumFilters: true // Add flag to clear premium filters
                    });
                }

                // Reset success message after 3 seconds
                setTimeout(() => setSuccess(false), 3000);
            } else {
                throw new Error(data.error || 'Failed to save pricing formula');
            }
        } catch (err) {
            console.error('Error saving fee:', err);
            setError(err.message || 'Failed to save pricing formula');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        setError(null);
        setSuccess(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError(null);

        // Restore existing values if they exist
        if (existingFee) {
            setFormData(prev => ({
                ...prev,
                msrpMultiplier: existingFee.msrp_multiplier.toString(),
                finalMultiplier: existingFee.final_multiplier.toString(),
                premiumListing: existingFee.premium_listing === 1,
                premiumMsrp: existingFee.premium_msrp ? existingFee.premium_msrp.toString() : '',
                premiumMsrpMultiplier: existingFee.premium_msrp_multiplier ? existingFee.premium_msrp_multiplier.toString() : '',
                premiumFinalMultiplier: existingFee.premium_final_multiplier ? existingFee.premium_final_multiplier.toString() : ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                msrpMultiplier: '',
                finalMultiplier: '',
                premiumListing: false,
                premiumMsrp: '',
                premiumMsrpMultiplier: '',
                premiumFinalMultiplier: ''
            }));
        }
    };


    const getCountryDisplayName = (countryCode) => {
        const country = countries.find(c => c.code === countryCode);
        return country ? `${country.flag} ${country.name}` : countryCode;
    };

    // Helper function to convert kg to lbs
    const convertKgToLbs = (kg) => {
        return (kg * 2.20462).toFixed(2);
    };

    // Helper function to format weight range display in lbs
    const formatWeightRangeLbs = (minKg, maxKg) => {
        const minLbs = convertKgToLbs(minKg);
        const maxLbs = convertKgToLbs(maxKg);
        return `${minLbs} - ${maxLbs} lbs`;
    };

    // Helper function to format weight range display in kgs
    const formatWeightRangeKgs = (minKg, maxKg) => {
        return `${minKg} - ${maxKg} kg`;
    };

    return (
        <>
            <style>
                {`
                    .no-arrows::-webkit-outer-spin-button,
                    .no-arrows::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                    
                    .no-arrows {
                        -moz-appearance: textfield;
                    }
                `}
            </style>

            <Card className="shadow-sm border-primary mb-4">
                <Card.Header className="bg-secondary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                            {getCountryDisplayName(formData.countryCode)} Pricing Formula
                        </h6>
                        {!isEditing && (
                            <Button
                                variant="outline-light"
                                size="sm"
                                onClick={handleEdit}
                            >
                                ✏️ Edit
                            </Button>
                        )}
                    </div>
                </Card.Header>
                <Card.Body>
                    {success && (
                        <Alert variant="success" className="mb-3">
                            ✅ Pricing formula saved.
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger" className="mb-3">
                            ❌ {error}
                        </Alert>
                    )}

                    {isEditing ? (
                        <Form>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Select
                                            value={formData.countryCode}
                                            onChange={(e) => handleCountryChange(e.target.value)}
                                            disabled={isLoading}
                                        >
                                            {countries.map((country) => (
                                                <option key={country.code} value={country.code}>
                                                    {country.flag} {country.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            <strong>MSRP Multiplier</strong>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            placeholder="e.g., 1.25"
                                            value={formData.msrpMultiplier}
                                            onChange={(e) => handleInputChange('msrpMultiplier', e.target.value)}
                                            disabled={isLoading}
                                            className="no-arrows"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            <strong>Final Multiplier</strong>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            placeholder="e.g., 1.10"
                                            value={formData.finalMultiplier}
                                            onChange={(e) => handleInputChange('finalMultiplier', e.target.value)}
                                            disabled={isLoading}
                                            className="no-arrows"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Premium Listing Section */}
                            <div className="mb-3 p-3 bg-light rounded border">
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        id="premiumListing"
                                        label={
                                            <span>
                                                <strong>💎 Premium Listing Support</strong>
                                                <small className="text-muted d-block">
                                                    Enable premium fees for high-value products
                                                </small>
                                            </span>
                                        }
                                        checked={formData.premiumListing}
                                        onChange={(e) => handlePremiumListingChange(e.target.checked)}
                                        disabled={isLoading}
                                    />
                                </Form.Group>

                                {formData.premiumListing && (
                                    <>
                                        <Row>
                                            <Col md={12}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <strong>MSRP Threshold</strong>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        placeholder="e.g., 100.00"
                                                        value={formData.premiumMsrp}
                                                        onChange={(e) => handleInputChange('premiumMsrp', e.target.value)}
                                                        disabled={isLoading}
                                                        className="no-arrows"
                                                        style={{ maxWidth: '150px' }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <strong>MSRP Multiplier</strong>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        placeholder="e.g., 1.35"
                                                        value={formData.premiumMsrpMultiplier}
                                                        onChange={(e) => handleInputChange('premiumMsrpMultiplier', e.target.value)}
                                                        disabled={isLoading}
                                                        className="no-arrows"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        <strong>Final Multiplier</strong>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        placeholder="e.g., 1.15"
                                                        value={formData.premiumFinalMultiplier}
                                                        onChange={(e) => handleInputChange('premiumFinalMultiplier', e.target.value)}
                                                        disabled={isLoading}
                                                        className="no-arrows"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    onClick={handleSave}
                                    disabled={isSaving || isLoading}
                                >
                                    {isSaving ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        '💾 Save'
                                    )}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleCancel}
                                    disabled={isSaving || isLoading}
                                >
                                    Cancel
                                </Button>
                            </div>

                            {/* Shipping Fees Display */}
                            {existingFee?.shipping_fees && (
                                <div className="mt-4">
                                    <Form.Group>
                                        <Form.Label>
                                            <strong>Shipping Fees</strong>
                                        </Form.Label>

                                        {/* Weight ranges in lbs */}
                                        <Form.Select
                                            size="sm"
                                            className="bg-light mb-2"
                                        >
                                            <option>Weight Range (lbs) → Shipping Cost</option>
                                            {existingFee.shipping_fees.map((fee, index) => (
                                                <option key={index}>
                                                    {formatWeightRangeLbs(fee.weight_min, fee.weight_max)} → ${fee.shipping_fee}
                                                </option>
                                            ))}
                                        </Form.Select>

                                        {/* Weight ranges in kgs */}
                                        <Form.Select
                                            size="sm"
                                            className="bg-light"
                                        >
                                            <option>Weight Range (kgs) → Shipping Cost</option>
                                            {existingFee.shipping_fees.map((fee, index) => (
                                                <option key={index}>
                                                    {formatWeightRangeKgs(fee.weight_min, fee.weight_max)} → ${fee.shipping_fee}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            )}
                        </Form>
                    ) : (
                        <div>
                            {/* Loading State */}
                            {isLoading && (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" variant="primary" />
                                    <p className="mt-2 text-muted">Loading fee data...</p>
                                </div>
                            )}

                            {/* Display Current Formula */}
                            {!isLoading && (
                                <div className="mb-3">
                                    <div className="text-center p-3 bg-light rounded">
                                        <code className="fs-6">
                                            {formData.premiumListing ? (
                                                <>
                                                    <div>Classic<br />((MSRP + Shipping {formData.countryCode}) × {formData.msrpMultiplier || '-'}) × {formData.finalMultiplier || '-'}</div>
                                                    <div className="text-primary mt-1">
                                                        Premium (MSRP &gt; {formData.premiumMsrp || '-'} USD)<br />((MSRP + Shipping {formData.countryCode}) × {formData.premiumMsrpMultiplier || '-'}) × {formData.premiumFinalMultiplier || '-'}
                                                    </div>
                                                </>
                                            ) : (
                                                `((MSRP + Shipping ${formData.countryCode}) × ${formData.msrpMultiplier || '-'}) × ${formData.finalMultiplier || '-'}`
                                            )}
                                        </code>
                                    </div>
                                </div>
                            )}

                            {/* Current Settings */}
                            {!isLoading && (
                                <>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <div className="text-center p-2 bg-primary bg-opacity-10 rounded">
                                                <small className="text-muted d-block">MSRP Multiplier</small>
                                                <Badge bg="primary" className="fs-6">
                                                    {formData.msrpMultiplier || '0.00'}x
                                                </Badge>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                                                <small className="text-muted d-block">Final Multiplier</small>
                                                <Badge bg="success" className="fs-6">
                                                    {formData.finalMultiplier || '0.00'}x
                                                </Badge>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Premium Settings Display */}
                                    {formData.premiumListing && (
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <div className="text-center p-3 bg-warning bg-opacity-10 rounded border">
                                                    <small className="text-muted d-block mb-2">💎 Premium Listing Active</small>
                                                    <Row>
                                                        <Col md={4}>
                                                            <small className="text-muted d-block">MSRP Threshold</small>
                                                            <Badge bg="warning" text="dark" className="fs-6">
                                                                ${formData.premiumMsrp || '0.00'}
                                                            </Badge>
                                                        </Col>
                                                        <Col md={4}>
                                                            <small className="text-muted d-block">MSRP Multiplier</small>
                                                            <Badge bg="warning" text="dark" className="fs-6">
                                                                {formData.premiumMsrpMultiplier || '0.00'}x
                                                            </Badge>
                                                        </Col>
                                                        <Col md={4}>
                                                            <small className="text-muted d-block">Final Multiplier</small>
                                                            <Badge bg="warning" text="dark" className="fs-6">
                                                                {formData.premiumFinalMultiplier || '0.00'}x
                                                            </Badge>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default CategoryPricing;
